import * as React from "react";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { Instance as TippyInstance } from "tippy.js";
import { createCollaborateSuggestionTippy } from "./collaborateSuggestionTippy";
import CollaborateMentionList, {
  type CollaborateMentionListRef,
} from "../components/editor/CollaborateMentionList";
import type { CollaborateUser } from "../types/collaborate";
import { filterCollaborateUsers, userToMentionItem } from "./collaborateMentions";
import { setSignoffPending } from "./collaborateEditorStorage";
import {
  hasSignoffBadge,
  removeSignoffAssigneeNodes,
  removeSignoffBadgeNodes,
} from "./collaborateSignoffChips";

export function createCollaborateSignoffSuggestion(
  getUsers: () => CollaborateUser[]
): Omit<SuggestionOptions, "editor"> {
  return {
    char: "@",
    allowSpaces: false,
    allow: ({ editor }) => hasSignoffBadge(editor),
    items: ({ query }) =>
      filterCollaborateUsers(getUsers(), query)
        .slice(0, 8)
        .map(userToMentionItem),
    command: ({ editor, range, props }) => {
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      if (nodeAfter?.text?.startsWith(" ")) {
        range.to += 1;
      }

      // Delete @ + query while range is still valid (badge removal would shift positions).
      editor.chain().focus().deleteRange(range).run();

      removeSignoffBadgeNodes(editor);
      removeSignoffAssigneeNodes(editor);

      const insertPos = editor.state.selection.from;
      editor
        .chain()
        .focus()
        .insertContentAt(insertPos, [
          {
            type: "signoff",
            attrs: { id: props.id, label: props.label },
          },
          { type: "text", text: " " },
        ])
        .run();
      setSignoffPending(editor, false);
    },
    render: () => {
      let component: ReactRenderer<CollaborateMentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props) => {
          component = new ReactRenderer(CollaborateMentionList, {
            props: {
              items: props.items,
              command: props.command,
              heading: "Request sign-off from",
              showHint: true,
            },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = createCollaborateSuggestionTippy({
            getReferenceClientRect: props.clientRect as () => DOMRect,
            content: component.element,
          });
        },

        onUpdate(props) {
          component?.updateProps({
            items: props.items,
            command: props.command,
            heading: "Request sign-off from",
            showHint: true,
          });
          if (popup?.[0] && props.clientRect) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          }
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit() {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        },
      };
    },
  };
}
